import type { Resource } from '@/db/types'
import { compact, conflict, invalid } from '@/kinds/kind'
import { formatTime } from '@/lib/time'
import { fileShareOf, type GrantSpec, type GrantStatus } from './schema'

export type GrantResource = Resource<GrantSpec, GrantStatus>

export const defaults = (spec: Partial<GrantSpec>): GrantSpec => ({
  name: spec.name ?? '',
  scopes: spec.scopes ?? [],
  token_hash: spec.token_hash ?? '',
  ...spec,
})

export const validate = (r: GrantResource): string[] => {
  const errs: string[] = []
  if (!r.spec.name.trim()) errs.push('name is required')
  if (!r.spec.scopes.length) errs.push('at least one scope')
  if (r.spec.token_hash.length !== 64) errs.push('token_hash must be hex sha256')
  // a Claude Code client's key only proposes (mirrors grant.Validate)
  if (r.spec.scopes.includes('mcp')) {
    if (!r.spec.api) errs.push('mcp belongs to an API token')
    if (r.spec.writes === 'direct')
      errs.push("a Claude Code key's changes wait for the owner: writes must be suggest")
    for (const sc of ['lead', 'prime', 'capture', 'calendar'])
      if (r.spec.scopes.includes(sc)) errs.push(`a Claude Code key cannot also hold ${sc}`)
  }
  // a sync agent's key: direct writes and its one scope (mirrors grant.Validate)
  if (r.spec.scopes.some((sc) => sc.startsWith('files-sync:'))) {
    if (!r.spec.api || r.spec.writes !== 'direct') errs.push('a sync key is an API token with direct writes')
    if (r.spec.scopes.length !== 1) errs.push('a sync key holds its one files-sync scope only')
  }
  return [...errs, ...fileShareProblems(r.spec)]
}

/** Mirrors grant.validateFileShare: one file scope, one purpose, its options only where they mean something. */
export const fileShareProblems = (s: GrantSpec): string[] => {
  const fs = fileShareOf(s)
  if (!fs) {
    if (s.password_hash || s.max_downloads || s.view_only || s.drop_max_bytes || s.drop_max_files)
      return ['password, downloads, view-only and request limits belong to a file share']
    if (s.scopes.some((sc) => sc.startsWith('file:'))) return ['a file share holds its one file scope only']
    return []
  }
  if (s.scopes.length !== 1) return ['a file share holds its one file scope only']
  if (s.api || s.max_uses) return ['a file share is a link: no API, no use limit (use max_downloads)']
  if (fs.drop) {
    if (s.password_hash || s.max_downloads || s.view_only)
      return ['a file request takes files: no password, downloads or view-only']
  } else if (s.drop_max_bytes || s.drop_max_files) return ['request limits belong to a file request']
  return []
}

/** A Claude Code client's key: an API token with mcp and not the lead's (mirrors grant.IsClient). */
export const isClientKey = (spec: { api?: boolean; scopes: string[] }): boolean =>
  !!spec.api && spec.scopes.includes('mcp') && !spec.scopes.includes('lead')

export const initialStatus = (): GrantStatus => ({ use_count: 0 })

export const displayName = (r: GrantResource): string => r.spec.name.trim() || 'Share'

/** Whether the grant can still be presented now (mirrors grant.Live). */
export const isLive = (r: GrantResource, now = Date.now()): { ok: boolean; why?: string } => {
  if (r.status.revoked_at) return { ok: false, why: 'revoked' }
  if (r.spec.expires_at && Date.parse(r.spec.expires_at) <= now) return { ok: false, why: 'expired' }
  if (r.spec.max_uses && r.status.use_count >= r.spec.max_uses) return { ok: false, why: 'used up' }
  if (r.spec.max_downloads && (r.status.downloads ?? 0) >= r.spec.max_downloads)
    return { ok: false, why: 'used up' }
  return { ok: true }
}

/** Mirrors internal/kinds/grant Apply. Fixtures: testdata/actions/grant_*.json. */
export const apply = (r: GrantResource, action: string, _args: unknown, at: string): GrantResource => {
  const st: GrantStatus = { ...r.status }
  if (action === 'revoke') {
    if (st.revoked_at) throw conflict('already revoked')
    st.revoked_at = formatTime(new Date(at))
  } else if (action === 'use') {
    if (st.revoked_at) throw conflict('revoked')
    if (r.spec.max_uses && st.use_count >= r.spec.max_uses) throw conflict('used up')
    st.use_count = st.use_count + 1
    st.last_used_at = formatTime(new Date(at))
  } else if (action === 'download') {
    if (st.revoked_at) throw conflict('revoked')
    if (r.spec.max_downloads && (st.downloads ?? 0) >= r.spec.max_downloads) throw conflict('used up')
    st.downloads = (st.downloads ?? 0) + 1
  } else if (action === 'drop') {
    if (st.revoked_at) throw conflict('revoked')
    if (r.spec.drop_max_files && (st.drops ?? 0) >= r.spec.drop_max_files) throw conflict('full')
    st.drops = (st.drops ?? 0) + 1
  } else throw invalid(`unknown action ${action}`)
  return { ...r, status: compact(st) }
}

// ---- secrets: made on the device, hashed before they leave it ---------------

const b64url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

/** A fresh 32-byte secret, base64url (43 chars), like the server's grant.NewSecret. */
export const newSecret = (): string => b64url(crypto.getRandomValues(new Uint8Array(32)))

/** hex(sha256(secret)), the only form that is ever stored. */
export const hashSecret = async (secret: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(secret))
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}
